import React from "react";
import {
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import * as Sentry from "@sentry/react";
import { Employee, EmployeeService } from "../../api/generated";
import "cally";
import { format } from "date-fns";
import { ymdDash } from "../search_bar";

interface EmployeeFormProps {
  employee: Employee;
  onDelete?: () => void;
  onSave?: (employeeId: number) => void;
  allowDelete?: boolean;
  disabled?: boolean;
}

export const EmployeeForm = (props: EmployeeFormProps) => {
  const [isBirthDatePopoverOpen, setIsBirthDatePopoverOpen] =
    React.useState(false);

  const { register, handleSubmit, control, setValue, watch } =
    useForm<Employee>({
      defaultValues: props.employee,
    });

  const birthDateValue = watch("birthDate");
  const birthDateButtonLabel = React.useMemo(() => {
    if (!birthDateValue) {
      return "Kies een datum";
    }

    const parsedDate = new Date(birthDateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return "Kies een datum";
    }

    return new Intl.DateTimeFormat("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(parsedDate);
  }, [birthDateValue]);

  const handleBirthDateChange = (event: CustomEvent) => {
    console.log(event);
    const selectedDate = (event.target as { value?: string } | null)?.value;

    if (!selectedDate) {
      return;
    }

    const formattedDate = format(selectedDate, ymdDash);

    setValue("birthDate", formattedDate, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setIsBirthDatePopoverOpen(false);
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "straps",
  });

  const onSubmit: SubmitHandler<Employee> = (data) => {
    console.log(data);
    if (data.id) {
      EmployeeService.putEmployee(data.id, data)
        .then((response) => {
          if (props.onSave) {
            props.onSave(response.id!);
          }
        })
        .catch((error) => {
          console.error("Error updating employee:", error);
          Sentry.captureException(error);
        });
    } else {
      EmployeeService.postEmployee(data)
        .then((response) => {
          if (props.onSave) {
            props.onSave(response.id!);
          }
        })
        .catch((error) => {
          console.error("Error creating employee:", error);
          Sentry.captureException(error);
        });
    }
  };
  const onError: SubmitErrorHandler<any> = (errors) => console.log(errors);

  async function deleteEmploy() {
    // if (confirm("Weet je zeker dat je deze medewerker wilt verwijderen?")) {
    //   await deleteEmployee(props.employee.id!);
    //   if (props.onDelete) {
    //     props.onDelete();
    //   }
    // }
  }

  return (
    <div className="card w-full bg-base-200 shadow-xl mb-5">
      <div className="card-body">
        <h2>Medewerker</h2>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Voornaam</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                required
                {...register("firstName", { required: true })}
                disabled={props.disabled === true}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Achternaam</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                required
                {...register("lastName", { required: true })}
                disabled={props.disabled === true}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Geboortedatum</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="input input-bordered w-full cursor-pointer text-left font-normal disabled:cursor-not-allowed"
                  id="cally1"
                  onClick={() =>
                    setIsBirthDatePopoverOpen((currentValue) => !currentValue)
                  }
                  disabled={props.disabled === true}
                >
                  {birthDateButtonLabel}
                </button>
                <div
                  id="cally-popover1"
                  className={`absolute left-0 top-full mt-2 z-50 bg-base-100 rounded-box shadow-lg ${
                    isBirthDatePopoverOpen ? "block" : "hidden"
                  }`}
                >
                  <calendar-date
                    className="cally"
                    onchange={handleBirthDateChange}
                  >
                    <svg
                      aria-label="Previous"
                      className="fill-current size-4"
                      slot="previous"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                    >
                      <path d="M15.75 19.5 8.25 12l7.5-7.5"></path>
                    </svg>
                    <svg
                      aria-label="Next"
                      className="fill-current size-4"
                      slot="next"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                    >
                      <path d="m8.25 4.5 7.5 7.5-7.5 7.5"></path>
                    </svg>
                    <calendar-month></calendar-month>
                  </calendar-date>
                </div>
              </div>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Coordinator</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                {...register("coordinator")}
                disabled={props.disabled === true}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Rol</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                {...register("role")}
                disabled={props.disabled === true}
              />
            </div>
          </div>

          <div className="mt-6 w-1/2 min-w-75">
            <h3 className="text-lg font-semibold mb-4">Bandjes</h3>
            {fields.map((field, index) => (
              <div key={field.id} className="form-control mb-3">
                <label className="label">
                  <span className="label-text">Bandje {index + 1}</span>
                </label>
                <div className="join w-full">
                  <input
                    type="text"
                    className="input input-bordered join-item w-full"
                    {...register(`straps.${index}`)}
                    disabled={props.disabled === true}
                  />
                  <button
                    type="button"
                    className="btn btn-error join-item"
                    onClick={() => remove(index)}
                    disabled={props.disabled === true}
                  >
                    Verwijder
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-secondary mt-2"
              onClick={() => append("")}
              disabled={props.disabled === true}
            >
              Bandje toevoegen
            </button>
          </div>
          <div className="card-actions mt-4 flex justify-end gap-2">
            {props.allowDelete === true && (
              <button
                type="button"
                className="btn btn-error"
                onClick={deleteEmploy}
              >
                Verwijder
              </button>
            )}
            <input
              type="submit"
              className="btn btn-primary"
              value="Opslaan"
              disabled={props.disabled === true}
            />
          </div>
        </form>
      </div>
    </div>
  );
};
